import {
  ORDER_INQUIRY_KIND_LABELS,
  ORDER_INQUIRY_RESOLUTION_LABELS,
  ORDER_INQUIRY_STATUS_LABELS,
  type OrderInquiry,
} from '@repo/api-contracts';
import type { ReactElement, ReactNode } from 'react';
import { formatDateTime } from '@/shared/lib';

interface OrderInquiryCardProps {
  readonly inquiry: OrderInquiry;
}

interface DetailRow {
  readonly label: string;
  readonly value: ReactNode;
}

const PURGED_LABEL = '보관 기간이 지난 사진';

const rowsOf = (inquiry: OrderInquiry): DetailRow[] => [
  { label: '내용', value: <span className="whitespace-pre-wrap">{inquiry.content}</span> },
  {
    label: '접수 일시',
    value: <span className="tabular-nums">{formatDateTime(inquiry.createdAt)}</span>,
  },
  ...(inquiry.images.length > 0
    ? [
        {
          label: '사진',
          value: (
            <ul className="flex flex-wrap gap-2">
              {inquiry.images.map((image, index) => (
                <li key={image.id}>
                  {image.url === null ? (
                    <span
                      role="img"
                      aria-label={PURGED_LABEL}
                      className="flex size-20 items-center justify-center bg-muted p-1 text-center text-[11px] leading-tight text-muted-foreground"
                    >
                      {PURGED_LABEL}
                    </span>
                  ) : (
                    <img
                      src={image.url}
                      alt={`문의 사진 ${index + 1}`}
                      className="size-20 object-cover"
                    />
                  )}
                </li>
              ))}
            </ul>
          ),
        },
      ]
    : []),
  ...(inquiry.resolution !== null
    ? [
        {
          label: '처리 결과',
          value: <span>{ORDER_INQUIRY_RESOLUTION_LABELS[inquiry.resolution]}</span>,
        },
      ]
    : []),
  ...(inquiry.answer !== null
    ? [{ label: '답변', value: <span className="whitespace-pre-wrap">{inquiry.answer}</span> }]
    : []),
  ...(inquiry.answeredAt !== null
    ? [
        {
          label: '답변 일시',
          value: <span className="tabular-nums">{formatDateTime(inquiry.answeredAt)}</span>,
        },
      ]
    : []),
];

/** 문의 한 건 — 종류와 처리 상태(배지 칩), 내용·접수 일시·사진, 답변이 있으면 처리 결과·답변·답변 일시. */
export const OrderInquiryCard = ({ inquiry }: OrderInquiryCardProps): ReactElement => (
  <div className="flex flex-col gap-3 py-4 text-sm">
    <p className="flex flex-wrap items-center gap-2">
      <span className="font-semibold">{ORDER_INQUIRY_KIND_LABELS[inquiry.kind]}</span>{' '}
      <span className="inline-flex h-6 items-center border border-foreground/20 px-2 text-xs font-semibold whitespace-nowrap">
        {ORDER_INQUIRY_STATUS_LABELS[inquiry.status]}
      </span>
    </p>
    <dl className="flex flex-col gap-1 wrap-anywhere">
      {rowsOf(inquiry).map((row) => (
        <div key={row.label} className="flex gap-4">
          <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">{row.label}</dt>
          <dd className="min-w-0 flex-1">{row.value}</dd>
        </div>
      ))}
    </dl>
  </div>
);
